import { shallowRef, ref, computed, watch } from 'vue'
import type { ComputedRef } from 'vue'
import { mergeResources, type AllResources, type Included, type Resource } from '@/api/resources'
import type { components } from '@/api/schema'

export type PaginationMeta = components['schemas']['PaginationMeta']

// P is constrained to the JSON:API pagination shape so loadMore can compose
// `page.number` without discarding a caller-chosen `page.size` (and without a
// cast — every generated *QueryParams type carries this page member). The
// `| undefined` arm exists because the generated types make the whole query
// object optional (`paths[...]['parameters']['query']` is `{...} | undefined`).
type Paged = { page?: { number?: number; size?: number } }

export interface PaginationOptions {
  immediate?: boolean
  // OPT-IN: make a reactive-params CHANGE loud (flip `loading`, showing the
  // skeleton) instead of the default silent in-place swap. Reserved for a
  // FILTER change the user just made and expects feedback on; it never affects
  // the silent refetch()/reconcile path (mutations, live updates), which passes
  // the same params and must not flash. Off by default so search-as-you-type
  // stays flash-free on the list views that want it that way.
  loudOnParamChange?: boolean
  // For a list that GROWS its window rather than replacing it: a change to
  // `page` alone swaps rows in place, while a real filter change still flashes.
  // ⚠ Opt-in, because the distinction is replace-vs-grow and not page-vs-filter:
  // a numbered-page table wants its skeleton when you move to page 2, since
  // every row is about to be different. A list that re-asks for MORE of the same
  // rows does not — the ones you are reading are still going to be there.
  quietOnPageChange?: boolean
}

export function usePagination<T extends AllResources, P extends Paged | undefined = Paged>(
  fetcher: (params: Partial<P>) => Promise<{
    data: T[]
    included?: Resource<Included<T['type']>>[]
    // The server's PaginationMeta, passed through verbatim (footer math —
    // total/pages/from/to — is server-derived state, never client-computed).
    // Optional only because the generated response types keep `meta` optional.
    meta?: PaginationMeta
  } | null>,
  reactiveParams?: ComputedRef<Partial<P>>,
  options?: PaginationOptions
) {
  // A shallowRef: the planning payloads are large and deep reactivity over
  // them is not worth its cost. The consequence is that ONLY replacing `.value`
  // renders — an in-place `rows.value[i] = x` or `.unshift(x)` is invisible to
  // every template reading the list. So the list is exposed READ-ONLY below
  // (`data` is a computed) and the three writers a composable needs live here,
  // each of them a reassignment. A composable that spells the write any other
  // way fails to type, which is the point.
  const rows = shallowRef<T[]>([])
  const data = computed(() => rows.value)
  const included = shallowRef<Resource<Included<T['type']>>[]>([])
  const currentPage = ref(1)
  const serverMeta = shallowRef<PaginationMeta | null>(null)
  const currentParams = ref<Partial<P>>({})
  // First-load-loud by default: `loading` flips until the first fetch settles,
  // so views gate their skeleton on it and every subsequent refetch (mutation
  // reconcile, live update) swaps data in place with no flash. A caller may
  // additionally opt a FILTER change into loud (loudOnParamChange) — the one
  // case where a flash is the point. `fetching` is true during ANY in-flight
  // fetch — for activity cues, and for awaiting the reactive-params refetch a
  // URL change kicks off (the planning board's cross-week suggestion placement).
  const loading = ref(false)
  const fetching = ref(false)
  const loadingMore = ref(false)
  const ready = ref(false)
  // The last list fetch failed (the fetcher answered null). Without it a list
  // that could not load is indistinguishable from an empty one, and says
  // « nothing here » when the truth is « could not ask ». Opt-in: only a
  // surface that reads it changes; a later success clears it. A failed
  // loadMore does not set it — the rows already on screen are still true.
  const failed = ref(false)

  const hasMore = computed(() => rows.value.length < (serverMeta.value?.total ?? 0))
  // Null until the first response lands — consumers guard, never invent zeros.
  const meta = computed(() => serverMeta.value)

  // Monotonic fetch counter: overlapping fetches (week nav + a live-update
  // refetch, solve-then-refetch, …) resolve out of order, and a stale response
  // must never overwrite a newer one's data.
  let fetchSeq = 0

  // `loud` = show the skeleton, not just the activity flag. Always loud on the
  // first load; otherwise only when a caller opted a FILTER change into it.
  const _fetch = async (p: Partial<P>, loud = false) => {
    const seq = ++fetchSeq
    fetching.value = true
    if (!ready.value || loud) loading.value = true
    currentParams.value = p
    currentPage.value = 1
    try {
      const response = await fetcher(p)
      if (seq === fetchSeq) failed.value = !response
      if (response && seq === fetchSeq) {
        rows.value = response.data
        included.value = response.included || []
        serverMeta.value = response.meta ?? null
      }
    } finally {
      if (seq === fetchSeq) {
        fetching.value = false
        loading.value = false
        ready.value = true
      }
    }
  }

  const fetchPage = async (p: Partial<P> = {}) => {
    if (reactiveParams) return
    // p spreads LAST so a caller-supplied page (e.g. { size: 100 }) survives;
    // without an explicit number the server defaults to page 1 anyway.
    await _fetch({ page: { number: 1 }, ...p })
  }

  const refetch = () => {
    if (reactiveParams) return _fetch(reactiveParams.value)
    return _fetch(currentParams.value)
  }

  // Works in BOTH modes: reactive params set currentParams through _fetch, so
  // appending the next page composes with them. The seq capture is what makes
  // that safe — a params change (or any refetch) mid-flight bumps fetchSeq and
  // the stale page is dropped instead of appending onto the reset list.
  const loadMore = async () => {
    if (!hasMore.value || loadingMore.value) return

    const seq = fetchSeq
    loadingMore.value = true
    currentPage.value++

    try {
      const response = await fetcher({
        ...currentParams.value,
        page: { ...currentParams.value?.page, number: currentPage.value },
      })

      if (response && seq === fetchSeq) {
        rows.value = mergeResources<T>(rows.value, response.data)
        if (response.included) {
          included.value = mergeResources<Resource<Included<T['type']>>>(
            included.value,
            response.included
          )
        }
        // Refresh the meta from the page's own response: rows resolved by
        // another session shrink the population between pages, and a stale
        // total keeps hasMore true forever — an auto-advancing caller would
        // spin on empty pages, a manual « Voir plus » would show and do nothing.
        serverMeta.value = response.meta ?? serverMeta.value
      }
    } finally {
      loadingMore.value = false
    }
  }

  // The ONLY ways a composable writes the list after a mutation (create, patch,
  // delete) without a refetch. Each reassigns, so the shallowRef renders.
  const prependItem = (item: T) => {
    rows.value = [item, ...rows.value]
  }
  const replaceItem = (id: string, item: T) => {
    rows.value = rows.value.map(row => (row.id === id ? item : row))
  }
  const removeItem = (id: string) => {
    rows.value = rows.value.filter(row => row.id !== id)
  }
  // A single fetched record joins the list: replaced if the list already holds
  // it, appended (the list's own order is the server's) if it does not.
  const upsertItem = (item: T) => {
    rows.value = rows.value.some(row => row.id === item.id)
      ? rows.value.map(row => (row.id === item.id ? item : row))
      : [...rows.value, item]
  }

  if (reactiveParams) {
    // Value-dedup: a params computed re-evaluates into a FRESH object whenever
    // any of its reactive inputs changes identity — including a parent render
    // minting a new `?? []` prop — and Vue's watch compares sources with
    // Object.is, so without this guard every such re-evaluation refires an
    // identical fetch (the planning bank's doubled pool call). Serializing the
    // params makes a watch fire a GENUINE change, which is exactly the "filter
    // changed" signal loudOnParamChange wants. refetch() bypasses the guard on
    // purpose — a same-params reconcile (mutation, live update) must refetch.
    const loudOnParamChange = options?.loudOnParamChange ?? false
    const quietOnPageChange = options?.quietOnPageChange ?? false
    let lastParamsKey: string | undefined
    let lastFilterKey: string | undefined
    watch(reactiveParams, (p) => {
      const key = JSON.stringify(p)
      if (key === lastParamsKey) return
      lastParamsKey = key
      // Under quietOnPageChange the loudness question is asked of everything
      // EXCEPT `page`, so deepening a window is silent and changing what the
      // window is OVER is not.
      const filterKey = quietOnPageChange
        ? JSON.stringify(Object.entries(p ?? {}).filter(([ k ]) => k !== 'page'))
        : key
      const loud = loudOnParamChange && filterKey !== lastFilterKey
      lastFilterKey = filterKey
      void _fetch(p, loud)
    }, {
      immediate: options?.immediate ?? true,
      deep: true,
    })
  }

  return {
    data, included, meta, loading, fetching, loadingMore, hasMore, ready, failed,
    fetchPage, refetch, loadMore, prependItem, replaceItem, removeItem, upsertItem,
  }
}
